import { ArrowRight, BrainCircuit, Cpu, FlaskConical, Layers3, Mail, Rocket, Users, Workflow } from 'lucide-react';
import { MobileSiteMenu } from './components/MobileSiteMenu';
import { ProductMark, ThemeToggle } from './Landing';

export function About({ goHome, openWorkspace, theme, toggleTheme }: { goHome: () => void; openWorkspace: () => void; theme: 'dark' | 'light'; toggleTheme: () => void }) {
  return <div className={`about-page industrial-home theme-${theme}`}>
    <header className="about-site-header">
      <nav aria-label="About page navigation">
        <a className="about-brand-link" href="/" onClick={(event) => { event.preventDefault(); goHome(); }} aria-label="Return to Ortl homepage">
          <ProductMark />
        </a>
        <div className="nav-links">
          <a href="/" onClick={(event) => { event.preventDefault(); goHome(); }}>Home</a>
          <a href="#vision">Vision</a>
          <a href="#roadmap">Roadmap</a>
          <a href="#founders">Founders</a>
          <ThemeToggle theme={theme} toggle={toggleTheme} />
          <button className="nav-cta" onClick={openWorkspace}>Launch workbench <ArrowRight size={14} /></button>
        </div>
        <MobileSiteMenu items={[{ label: 'Home', href: '/', onSelect: goHome }, { label: 'Vision', href: '#vision' }, { label: 'Roadmap', href: '#roadmap' }, { label: 'Founders', href: '#founders' }]} openWorkspace={openWorkspace} theme={theme} toggleTheme={toggleTheme} />
      </nav>
    </header>

    <main>
      <section className="about-hero">
        <div className="about-hero-copy">
          <div className="about-status"><i></i> PRODUCT BETA / BUILD 01</div>
          <p className="about-kicker">ABOUT ORTL</p>
          <h1>Building clearer paths<br />from logic to silicon.</h1>
          <p>Ortl is an early-stage hardware engineering software company creating visual, intelligent tools for chip designers, engineering teams, and students. We are starting by making RTL easier to inspect, trace, and understand.</p>
          <div className="about-actions">
            <button className="primary" onClick={openWorkspace}>Explore the beta <ArrowRight size={15} /></button>
            <span>No AI-generated hardware · deterministic analysis first</span>
          </div>
        </div>
        <aside className="about-principles" aria-label="Ortl principles">
          <header><Cpu size={15} /> ENGINEERING PRINCIPLES</header>
          <ol>
            <li><span>01</span><div><b>Clarity before complexity</b><small>Make hardware behavior inspectable.</small></div></li>
            <li><span>02</span><div><b>Engineers stay in control</b><small>Source and inference remain traceable.</small></div></li>
            <li><span>03</span><div><b>Education meets practice</b><small>Professional tools should also teach.</small></div></li>
          </ol>
        </aside>
      </section>

      <section className="about-statement" id="vision">
        <div>
          <small>OUR VISION</small>
          <h2>Hardware design should be visual, explainable, and accessible.</h2>
        </div>
        <p>Our long-term vision is an integrated design environment that helps people move confidently from HDL to implementation. Ortl aims to connect RTL understanding, design exploration, verification insight, and eventually AI-assisted physical design in one coherent engineering workflow.</p>
      </section>

      <section className="beta-panel">
        <div className="beta-icon"><FlaskConical size={24} /></div>
        <div>
          <small>CURRENT RELEASE STATUS</small>
          <h2>Ortl is in public beta testing.</h2>
          <p>The current release focuses on RTL parsing, hardware-aware schematics, signal tracing, source mapping, examples, and design reporting. Results should be reviewed before production or sign-off use.</p>
        </div>
        <dl>
          <div><dt>Stage</dt><dd>BETA</dd></div>
          <div><dt>Focus</dt><dd>RTL VISUALIZATION</dd></div>
          <div><dt>Feedback</dt><dd>ACTIVE</dd></div>
        </dl>
      </section>

      <section className="about-roadmap" id="roadmap">
        <header>
          <small>PRODUCT DIRECTION</small>
          <h2>From RTL insight to intelligent physical design.</h2>
          <p>Features are delivered in stages. Roadmap items describe direction and are not yet available in the beta.</p>
        </header>
        <div className="roadmap-grid">
          <article className="is-current">
            <span>NOW / BETA</span>
            <Workflow size={21} />
            <h3>RTL intelligence</h3>
            <p>Interactive schematics, fan-in and fan-out tracing, source mapping, FSM inspection, educational examples, and engineering reports.</p>
            <code>AVAILABLE IN BETA</code>
          </article>
          <article>
            <span>NEXT</span>
            <Layers3 size={21} />
            <h3>Design understanding</h3>
            <p>Broader HDL coverage, deeper hierarchy, stronger analysis, collaboration, and richer verification-aware workflows.</p>
            <code>IN DEVELOPMENT</code>
          </article>
          <article>
            <span>FUTURE</span>
            <BrainCircuit size={21} />
            <h3>AI physical design</h3>
            <p>Assisted floorplanning, placement exploration, constraint guidance, PPA trade-off analysis, and explainable optimization support.</p>
            <code>PLANNED RESEARCH</code>
          </article>
          <article>
            <span>HORIZON</span>
            <Rocket size={21} />
            <h3>End-to-end hardware workspace</h3>
            <p>A connected environment spanning learning, RTL development, verification insight, and implementation decisions.</p>
            <code>LONG-TERM VISION</code>
          </article>
        </div>
      </section>

      <section className="founders" id="founders">
        <div className="founders-heading">
          <Users size={19} />
          <div>
            <small>FOUNDERS & DEVELOPERS</small>
            <h2>Built by hardware-focused makers.</h2>
          </div>
        </div>
        <div className="founder-grid">
          <article style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px', padding: '24px' }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px solid rgba(255, 107, 0, 0.45)',
              background: '#0e1117',
              boxShadow: '0 0 16px rgba(255, 107, 0, 0.15)',
              flexShrink: 0
            }}>
              <img 
                src="/hamza.jpg" 
                alt="Hamza Ali" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center 20%',
                  display: 'block'
                }} 
              />
            </div>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 4px 0' }}>Hamza Ali</h3>
              <p style={{ margin: '0 0 8px 0', opacity: 0.8, fontSize: '0.9rem' }}>Founder</p>
              <a className="founder-email" href="mailto:hamzaaly105@gmail.com" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Mail size={13} />hamzaaly105@gmail.com
              </a>
            </div>
          </article>

          <article style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px', padding: '24px' }}>
            <div style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '2px solid rgba(255, 107, 0, 0.45)',
              background: '#0e1117',
              boxShadow: '0 0 16px rgba(255, 107, 0, 0.15)',
              flexShrink: 0
            }}>
              <img 
                src="/muhammad.jpg" 
                alt="Muhammad" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center 20%',
                  display: 'block'
                }} 
              />
            </div>
            <div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 600, margin: '0 0 4px 0' }}>Muhammad</h3>
              <p style={{ margin: '0 0 8px 0', opacity: 0.8, fontSize: '0.9rem' }}>Founder</p>
              <a className="founder-email" href="mailto:muhammad86762@gmail.com" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Mail size={13} />muhammad86762@gmail.com
              </a>
            </div>
          </article>
        </div>
      </section>

      <section className="about-cta">
        <div>
          <small>ORTL BETA</small>
          <h2>Help shape a clearer future for hardware design.</h2>
          <p>Try the current workbench and follow the product as new engineering capabilities arrive.</p>
        </div>
        <button className="primary" onClick={openWorkspace}>Open Ortl <ArrowRight size={15} /></button>
      </section>
    </main>
  </div>;
}